import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Send } from "lucide-react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { cn } from "@/design-system/lib/utils";
import { collectionBundlesApi } from "@/api/collectionBundles";
import type { MaterialAudience } from "@/api/portalMaterials";
import type { LeadConversionTarget } from "@/api/leads";
import {
  BrokeragePicker,
  type BrokeragePickerResult,
} from "@/components/brokerages/BrokeragePicker";

// UAT FixBatch FIND-18 — Send Application Link modal. Lets the marketer pick
// which collection bundle(s) to seed into the new applicant's package, or send
// portal access only with no items yet.
//
// Lead Conversion Router — when `showTypeRouter` is set (the lead flow), a first
// "What should this lead become?" step routes the invite to an Independent
// broker / Junior broker / Brokerage firm, filtering the bundle list by the
// matching collection category. The broker-detail reuse (an existing broker,
// already typed) leaves the flag off and keeps today's single-step UI exactly.

// The bundle-list audience per target maps to the collection category the owner
// files each type's bundles under (Independent → the existing broker bundles).
const AUDIENCE_FOR_TARGET: Record<LeadConversionTarget, MaterialAudience> = {
  IndependentBroker: "Broker",
  JuniorBroker: "JuniorBroker",
  Brokerage: "Brokerage",
};

const TARGET_OPTIONS: { value: LeadConversionTarget; label: string; help: string }[] = [
  {
    value: "IndependentBroker",
    label: "Independent broker",
    help: "Paid directly to the broker; optionally attach a covering firm for tracking.",
  },
  {
    value: "JuniorBroker",
    label: "Junior broker",
    help: "Paid through a covering firm (required). Phoenix pays the firm; the firm pays the broker.",
  },
  {
    value: "Brokerage",
    label: "Brokerage firm",
    help: "The lead becomes the firm — creates a brokerage record + portal account.",
  },
];

// Broker surface (mode="broker") — the onboarding-time "Broker type" chooser.
// Two options only (no "Brokerage firm"), with the broker-specific payee copy.
const BROKER_TYPE_OPTIONS: { value: LeadConversionTarget; label: string; help: string }[] = [
  {
    value: "IndependentBroker",
    label: "Independent broker",
    help: "Paid directly by Phoenix — optionally attach a covering brokerage for tracking.",
  },
  {
    value: "JuniorBroker",
    label: "Junior broker",
    help: "Paid through a covering brokerage (required). Phoenix pays the firm; the firm pays the broker off-platform.",
  },
];

interface SendApplicationLinkModalProps {
  open: boolean;
  onClose: () => void;
  leadEmail: string | null;
  /** Whether the current user can manage collection bundles — gates the
   *  "Manage bundles in admin" deep-link. Decided by the parent (which holds
   *  the auth context) so the modal stays presentational. */
  canManageBundles?: boolean;
  /** Lead Conversion Router — show the type chooser + brokerage picker (the lead
   *  flow). Off (default) keeps the single-step existing-broker UI unchanged. */
  showTypeRouter?: boolean;
  /** Surface variant. "broker" restricts the chooser to Independent / Junior
   *  (no "Brokerage firm"), uses the "Broker type" heading + broker payee copy,
   *  and defaults the chooser to `initialTarget`. "lead" (default) is the
   *  original 3-way lead-conversion router. */
  mode?: "lead" | "broker";
  /** Broker mode — the chooser's default selection (the broker's current type),
   *  re-applied each time the modal opens. */
  initialTarget?: LeadConversionTarget;
  onConfirm: (vars: {
    collectionBundleIds: string[];
    sendPortalAccessOnly: boolean;
    target: LeadConversionTarget;
    brokerageId: string | null;
  }) => void;
  /** In-flight state of the send mutation owned by the parent. Disables the
   *  buttons and shows a spinner so the invite can't be double-submitted. */
  submitting?: boolean;
}

export function SendApplicationLinkModal({
  open,
  onClose,
  leadEmail,
  canManageBundles = false,
  showTypeRouter = false,
  mode = "lead",
  initialTarget = "IndependentBroker",
  onConfirm,
  submitting = false,
}: SendApplicationLinkModalProps) {
  const [target, setTarget] = useState<LeadConversionTarget>(initialTarget);
  const [brokerage, setBrokerage] = useState<BrokeragePickerResult | null>(null);

  // Broker mode — re-seed the chooser to the broker's current type each open
  // (the lead flow keeps its persist-across-opens behaviour, untouched).
  useEffect(() => {
    if (open && mode === "broker") setTarget(initialTarget);
  }, [open, mode, initialTarget]);

  // Broker mode restricts the chooser to Independent / Junior (no Brokerage)
  // and swaps in the "Broker type" heading + broker payee copy.
  const typeOptions = mode === "broker" ? BROKER_TYPE_OPTIONS : TARGET_OPTIONS;
  const chooserHeading = mode === "broker" ? "Broker type" : "What should this lead become?";

  // The router maps the chosen target to its bundle category; the non-router
  // reuse keeps the canonical "Broker" audience (today's behaviour).
  const audience: MaterialAudience = showTypeRouter
    ? AUDIENCE_FOR_TARGET[target]
    : "Broker";

  const bundles = useQuery({
    queryKey: ["collection-bundles", "broker-invite", audience],
    queryFn: () => collectionBundlesApi.list(false, audience),
    enabled: open,
    // Bundle edits happen in the admin surface, which this modal opens in
    // a SEPARATE TAB ("Manage bundles in admin" below) — query invalidation
    // can't cross tabs, so the global 5-min staleTime would let this list
    // lag an admin edit. Always refetch on open instead.
    staleTime: 0,
  });

  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [portalOnly, setPortalOnly] = useState(false);

  // Initialise the selection from the fetched data. Independent keeps today's
  // rule (pre-check the seeded default bundle); Junior/Brokerage pre-check ALL
  // returned category bundles (they carry no isDefault flag). Keyed off the
  // fetched list + target so it re-seeds when the audience switches.
  useEffect(() => {
    if (!bundles.data) return;
    const preselectAll = target === "JuniorBroker" || target === "Brokerage";
    const next: Record<string, boolean> = {};
    for (const b of bundles.data) next[b.id] = preselectAll ? true : b.isDefault;
    setSelected(next);
  }, [bundles.data, target]);

  const selectedIds = useMemo(
    () => (bundles.data ?? []).filter((b) => selected[b.id]).map((b) => b.id),
    [bundles.data, selected],
  );

  const selectedBundles = useMemo(
    () => (bundles.data ?? []).filter((b) => selected[b.id]),
    [bundles.data, selected],
  );
  // Count only slots whose template is still active — instantiation skips
  // dangling slots (template retired without re-pointing), so counting them
  // here would promise items the invite can't create.
  const totalItems = selectedBundles.reduce(
    (sum, b) => sum + b.slots.filter((s) => s.templateActive).length,
    0,
  );

  // A junior must be attached to a covering firm (that is what activates the
  // firm-payee path). Independent's firm is optional; Brokerage has no picker.
  const juniorNeedsFirm = showTypeRouter && target === "JuniorBroker" && !brokerage;
  const canSend = (selectedIds.length > 0 || portalOnly) && !juniorNeedsFirm;

  const isBrokerageTarget = showTypeRouter && target === "Brokerage";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send application link"
      size="md"
      footer={
        <>
          <span className="mr-auto text-caption text-gray-mid">
            Selected: {totalItems} items across {selectedIds.length} bundles
          </span>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            size="sm"
            loading={submitting}
            disabled={!canSend}
            onClick={() =>
              onConfirm({
                collectionBundleIds: selectedIds,
                sendPortalAccessOnly: portalOnly,
                target: showTypeRouter ? target : "IndependentBroker",
                brokerageId: isBrokerageTarget ? null : brokerage?.brokerageId ?? null,
              })
            }
          >
            {submitting ? (
              "Sending…"
            ) : (
              <>
                <Send size={14} strokeWidth={1.5} aria-hidden /> Send invite
              </>
            )}
          </Button>
        </>
      }
    >
      <p className="text-small text-onyx">
        {isBrokerageTarget ? (
          <>
            We'll create a brokerage firm from {leadEmail ?? "the prospect"}'s
            company info and email them a one-time link to set up their firm
            portal account and complete the brokerage application.
          </>
        ) : (
          <>
            We'll email {leadEmail ?? "the prospect"} a one-time link that drops
            them into the broker application dashboard with their company and
            contact info pre-filled. This creates a broker record at Prospect
            status, carries over all lead data and history (tasks, communications,
            notes, activity), and runs territory routing for liaison assignment.
          </>
        )}
      </p>

      {showTypeRouter && (
        <div className="mt-space-4">
          <h3 className="font-display font-bold text-h5 text-onyx mb-space-2">
            {chooserHeading}
          </h3>
          <div
            className="flex flex-wrap gap-space-2"
            role="radiogroup"
            aria-label={mode === "broker" ? "Broker type" : "Conversion type"}
          >
            {typeOptions.map((o) => (
              <button
                key={o.value}
                type="button"
                role="radio"
                aria-checked={target === o.value}
                disabled={submitting}
                onClick={() => setTarget(o.value)}
                className={cn(
                  "px-space-3 py-space-2 rounded-md border text-small font-semibold transition-colors",
                  target === o.value
                    ? "border-gold bg-white-warm text-onyx shadow-goldRing"
                    : "border-border-light bg-white-pure text-gray-mid hover:border-gold",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
          <p className="text-caption text-gray-mid mt-space-2">
            {typeOptions.find((o) => o.value === target)?.help}
          </p>
        </div>
      )}

      {showTypeRouter && !isBrokerageTarget && (
        <div className="mt-space-4">
          <h3 className="font-display font-bold text-h5 text-onyx mb-space-2">
            Covering brokerage{target === "JuniorBroker" ? " (required)" : " (optional)"}
          </h3>
          {brokerage ? (
            <div className="flex items-center justify-between border border-border-light rounded-md p-space-3 bg-white-pure">
              <span className="text-small font-semibold text-onyx">
                {brokerage.brokerageName}
              </span>
              <Button
                variant="text"
                size="sm"
                onClick={() => setBrokerage(null)}
                disabled={submitting}
              >
                Change
              </Button>
            </div>
          ) : (
            <>
              <BrokeragePicker
                onSelect={setBrokerage}
                placeholder="Search or create a brokerage…"
                disabled={submitting}
              />
              {juniorNeedsFirm && (
                <p className="text-caption text-status-error mt-space-1">
                  A junior broker must be attached to a covering firm.
                </p>
              )}
            </>
          )}
        </div>
      )}

      <div className="mt-space-4">
        <div className="flex items-center justify-between mb-space-2">
          <h3 className="font-display font-bold text-h5 text-onyx">
            Application bundles
          </h3>
          {canManageBundles && (
            <a
              href="/admin/collection-bundles"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-space-1 text-caption text-gold-shadow hover:underline"
            >
              Manage bundles in admin
              <ExternalLink size={12} strokeWidth={1.5} aria-hidden />
            </a>
          )}
        </div>

        {bundles.isLoading && (
          <div className="text-caption uppercase tracking-wide text-gray-mid">
            Loading bundles…
          </div>
        )}
        {bundles.isError && (
          <div className="text-small text-status-error">
            Couldn't load bundles. Try again.
          </div>
        )}
        {bundles.data && bundles.data.length === 0 && (
          <div className="text-small text-gray-mid">
            No active bundles are configured for this type.
          </div>
        )}

        <div className="flex flex-col gap-space-2">
          {(bundles.data ?? []).map((b) => (
            <label
              key={b.id}
              className="flex items-start gap-space-2 border border-border-light rounded-md p-space-3 bg-white-pure cursor-pointer hover:border-gold"
            >
              <input
                type="checkbox"
                className="mt-[3px]"
                checked={!!selected[b.id]}
                onChange={(e) =>
                  setSelected((prev) => ({ ...prev, [b.id]: e.target.checked }))
                }
              />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-space-2">
                  <span className="text-small font-semibold text-onyx">
                    {b.name}
                  </span>
                  {(() => {
                    const usable = b.slots.filter((s) => s.templateActive).length;
                    const unavailable = b.slots.length - usable;
                    return (
                      <span className="text-caption text-gray-mid">
                        {usable} item{usable === 1 ? "" : "s"}
                        {unavailable > 0 && (
                          <span className="text-status-error">
                            {" "}
                            ({unavailable} unavailable)
                          </span>
                        )}
                      </span>
                    );
                  })()}
                </span>
                {b.description && (
                  <span className="block text-caption text-gray-mid mt-space-1">
                    {b.description}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>

      <label className="flex items-start gap-space-2 mt-space-4 cursor-pointer">
        <input
          type="checkbox"
          className="mt-[3px]"
          checked={portalOnly}
          onChange={(e) => setPortalOnly(e.target.checked)}
        />
        <span className="text-small text-onyx">
          Send portal access only — no items required yet
        </span>
      </label>
    </Modal>
  );
}
